/**
 * voice-v8 — Web-Push-Client (PWA Premium).
 *
 * - subscribe(): holt VAPID-Public-Key, fragt Notification-Permission,
 *   subscribed beim PushManager, schickt Sub an /api/push/subscribe
 * - unsubscribe(): unsubscribe lokal + Server
 * - getStatus(): aktueller Permission-Stand + Subscription
 */

export type PushPermission = "default" | "granted" | "denied" | "unsupported";

export interface PushStatus {
  permission: PushPermission;
  subscribed: boolean;
}

function urlBase64ToUint8Array(b64: string): Uint8Array {
  const padding = "=".repeat((4 - (b64.length % 4)) % 4);
  const base64 = (b64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

function isSupported(): boolean {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

async function getRegistration(): Promise<ServiceWorkerRegistration | null> {
  if (!isSupported()) return null;
  return (await navigator.serviceWorker.getRegistration()) ?? null;
}

export async function getStatus(): Promise<PushStatus> {
  if (!isSupported()) return { permission: "unsupported", subscribed: false };
  const reg = await getRegistration();
  const sub = reg ? await reg.pushManager.getSubscription() : null;
  return {
    permission: Notification.permission as PushPermission,
    subscribed: !!sub,
  };
}

/**
 * Gemeinsamer Registrier-Kern (DRY): Endpoint beschaffen (vorhandenes Abo
 * wiederverwenden, sonst neu anlegen — Permission muss bereits "granted"
 * sein, sonst promptet `subscribe` NICHT) und beim Server (idempotenter
 * Upsert per Endpoint) eintragen. Genutzt von `subscribe()` (mit
 * Permission-Frage davor) UND `resyncSubscription()` (still beim App-Start).
 */
async function registerWithServer(
  reg: ServiceWorkerRegistration,
): Promise<{ ok: boolean; error?: string }> {
  let publicKey: string;
  try {
    const keyRes = await fetch("/api/push/public-key");
    if (!keyRes.ok) throw new Error(`public-key-${keyRes.status}`);
    publicKey = (await keyRes.json()).publicKey as string;
  } catch (err) {
    return {
      ok: false,
      error: err instanceof Error ? err.message : "no-vapid-key",
    };
  }

  let sub: PushSubscription | null = await reg.pushManager.getSubscription();
  if (!sub) {
    try {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey)
          .buffer as ArrayBuffer,
      });
    } catch (err) {
      return {
        ok: false,
        error: err instanceof Error ? err.message : "subscribe-failed",
      };
    }
  }

  const res = await fetch("/api/push/subscribe", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "same-origin",
    body: JSON.stringify(sub.toJSON()),
  });
  if (!res.ok) {
    return { ok: false, error: `server-${res.status}` };
  }
  return { ok: true };
}

export async function subscribe(): Promise<{ ok: boolean; error?: string }> {
  if (!isSupported()) return { ok: false, error: "unsupported" };
  const reg = await getRegistration();
  if (!reg) return { ok: false, error: "no-service-worker" };

  const perm = await Notification.requestPermission();
  if (perm !== "granted") return { ok: false, error: `permission-${perm}` };

  return registerWithServer(reg);
}

/**
 * Selbstheilung gegen „Push zeigt aktiv, kommt aber nie an". Ursachen:
 * der Browser rotiert den Push-Endpoint still, der Server löscht eine Row
 * bei 410/404, ein Account-Wechsel ändert die userId, oder die DB wurde neu
 * aufgesetzt — das LOKALE Abo bleibt in all diesen Fällen bestehen, der
 * Server hat aber nichts mehr zum Senden. Diese Funktion läuft STILL beim
 * App-Start: ist die Erlaubnis erteilt, wird das (ggf. neu erzeugte) Abo
 * idempotent erneut beim Server eingetragen. Promptet NIE — bei fehlender
 * Erlaubnis passiert nichts. Best-effort, wirft nie nach außen.
 */
export async function resyncSubscription(): Promise<{
  ok: boolean;
  reason?: string;
}> {
  try {
    if (!isSupported()) return { ok: false, reason: "unsupported" };
    if (Notification.permission !== "granted")
      return { ok: false, reason: "not-granted" };
    // Auf den aktiven Service Worker warten (Boot-Race: register läuft evtl.
    // noch). `ready` löst auf, sobald die Registrierung aktiv ist.
    const reg = await navigator.serviceWorker.ready;
    const r = await registerWithServer(reg);
    return r.ok ? { ok: true } : { ok: false, reason: r.error };
  } catch (err) {
    return {
      ok: false,
      reason: err instanceof Error ? err.message : "resync-failed",
    };
  }
}

export async function unsubscribe(): Promise<void> {
  const reg = await getRegistration();
  const sub = reg ? await reg.pushManager.getSubscription() : null;
  if (!sub) return;
  await fetch("/api/push/unsubscribe", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "same-origin",
    body: JSON.stringify({ endpoint: sub.endpoint }),
  }).catch(() => {});
  await sub.unsubscribe().catch(() => {});
}

export async function sendTest(): Promise<{ sent: number; total: number }> {
  const res = await fetch("/api/push/test", {
    method: "POST",
    credentials: "same-origin",
  });
  return (await res.json()) as { sent: number; total: number };
}
